import { type ProductPenaltyRuleResult, ProductCancelSettingMode } from '~/view-models/product'

export function useProductPenaltyRule(_penaltyRule: MaybeRef<ProductPenaltyRuleResult | null>) {
  const penaltyRule = computed(() => unref(_penaltyRule))
  const i18n = useI18n()

  const cancelTimeLabel = computed(() => {
    const freeCancelRule = penaltyRule.value?.free_cancel
    if (!freeCancelRule) {
      return ''
    }
    switch (penaltyRule.value.mode) {
      case ProductCancelSettingMode.DAY: // day
        return freeCancelRule.day ? i18n.t('branch_info_free_cancellation_time_n_days', { number: freeCancelRule.day }) : ''
      case ProductCancelSettingMode.HOUR: // hour
        return freeCancelRule.hour ? i18n.t('branch_info_free_cancellation_time_n_hours', { number: freeCancelRule.hour }) : ''
      case ProductCancelSettingMode.ANY_TIME: // any time
        return i18n.t('branch_info_anytime_free_cancellation')
      default:
        return cancelTimeLabelForBooking.value
    }
  })

  const cancelTimeLabelForBooking = computed(() => {
    const freeCancelDate = penaltyRule.value?.free_cancel?.date
    if (!freeCancelDate) {
      return ''
    }
    const formattedDateTime = timeUtil.formatDateTime(freeCancelDate)
    return i18n.t('payment_free_cancellation_time', { date: formattedDateTime })
  })

  const cancelDepositLabel = computed(() => {
    const noShowRule = penaltyRule.value?.no_show
    if (!noShowRule?.amount) {
      return ''
    }
    return i18n.t('branch_notice_cancel_no_show_charge', {
      currency: noShowRule.currency_code,
      amount: Number(noShowRule.amount).toLocaleString('en-US'),
    })
  })

  const cancelSwappedDepositLabel = computed(() => {
    const noShowRule = penaltyRule.value?.no_show
    if (!noShowRule) {
      return ''
    }
    return i18n.t('payment_bind_card_exchange_currency', {
      currency: noShowRule.payment_currency_code,
      amount: Number(noShowRule.payment_amount).toLocaleString('en-US'),
    })
  })

  const creditCardDepositLabel = computed(() => i18n.t('payment_bind_credit_card'))

  const hasRule = computed(() => !!cancelTimeLabel.value || !!cancelDepositLabel.value)

  const hasPenaltyAmount = computed(() => {
    const noShowRule = penaltyRule.value?.no_show
    return noShowRule && (noShowRule.amount > 0 || noShowRule.payment_amount > 0)
  })

  return {
    hasRule,
    hasPenaltyAmount,
    cancelTimeLabel,
    cancelTimeLabelForBooking,
    cancelDepositLabel,
    cancelSwappedDepositLabel,
    creditCardDepositLabel,
  }
}
